import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getWorkOrderById, updateWorkOrder } from '../api';
import { WorkOrder, WorkOrderStatus } from '../types/workOrder';
import { useNotification } from '../contexts/NotificationContext';

const WorkOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotification();

  const fetchWorkOrder = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWorkOrderById(Number(id));
      setWorkOrder(data);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      addNotification('error', 'Failed to fetch work order details');
    } finally {
      setLoading(false);
    }
  }, [id, addNotification]);

  useEffect(() => {
    fetchWorkOrder();
  }, [fetchWorkOrder]);

  const handleStartWorkOrder = async () => {
    if (!workOrder) return;
    try {
      const updatedWorkOrder = await updateWorkOrder(workOrder.id, { status: WorkOrderStatus.IN_PROGRESS });
      setWorkOrder(updatedWorkOrder);
      addNotification('success', 'Work order started successfully');
    } catch (error) {
      console.error('Failed to start work order:', error);
      addNotification('error', 'Failed to start work order');
    }
  };

  const handleCompleteWorkOrder = async () => {
    if (!workOrder) return;
    try {
      const updatedWorkOrder = await updateWorkOrder(workOrder.id, { status: WorkOrderStatus.COMPLETED });
      setWorkOrder(updatedWorkOrder);
      addNotification('success', 'Work order completed successfully');
    } catch (error) {
      console.error('Failed to complete work order:', error);
      addNotification('error', 'Failed to complete work order');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!workOrder) {
    return <p>Work order not found.</p>;
  }

  return (
    <Card title="Work Order Details" className="max-w-800 m-auto">
      <div className="grid">
        <div className="col-12 md:col-6">
          <p><strong>Work Order ID:</strong> {workOrder.id}</p>
          <p><strong>Status:</strong> {workOrder.status}</p>
        </div>
        <div className="col-12 md:col-6">
          <p><strong>Created At:</strong> {new Date(workOrder.createdAt).toLocaleString()}</p>
          <p><strong>Updated At:</strong> {new Date(workOrder.updatedAt).toLocaleString()}</p>
        </div>
        <div className="col-12">
          <p><strong>Purchase Order:</strong> {workOrder.purchaseOrder.poNumber}</p>
        </div>
        <div className="col-12">
          <h3>Actions</h3>
          {workOrder.status === WorkOrderStatus.PENDING && (
            <Button label="Start Work Order" icon="pi pi-play" onClick={handleStartWorkOrder} className="mr-2" />
          )}
          {workOrder.status === WorkOrderStatus.IN_PROGRESS && (
            <Button label="Complete Work Order" icon="pi pi-check" onClick={handleCompleteWorkOrder} className="p-button-success" />
          )}
        </div>
      </div>
    </Card>
  );
};

export default WorkOrderDetails;